import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { PhotoAsk } from '@goaltree/backend/convex/lib/habitDots'
import { PHOTO_BACKFILL_DAYS } from '@goaltree/backend/convex/lib/habitPhotos'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@goaltree/ui/components/dropdown-menu'
import { Camera, Check } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PhotoCheckinSheet } from '@/components/dot-photo'
import { useMarkDay } from '@/hooks/use-mark-yesterday'
import { keyToDate, todayKey } from '@/lib/day'

type Habit = {
  _id: Id<'nodes'>
  title: string
  category?: string
  photoAsk?: PhotoAsk
  dot?: { kind: string } | null
}

// Tapping a recent missed dot: mark that day done, or done with a photo.
export function MissedDayMenu({
  habit,
  day,
  anchor,
  onClose,
}: {
  habit: Habit
  day: string | null
  anchor: HTMLElement | null
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const markDay = useMarkDay()
  const [photoDay, setPhotoDay] = useState<string | null>(null)
  const photoOk =
    !!day &&
    Math.round((keyToDate(todayKey()).getTime() - keyToDate(day).getTime()) / 86_400_000) <=
      PHOTO_BACKFILL_DAYS
  const label =
    day &&
    keyToDate(day).toLocaleDateString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  // The menu and photo sheet portal out, but React still bubbles their clicks
  // through this component to the habit row, which would open its side sheet.
  return (
    <span className="contents" onClick={stop} onPointerDown={stop} onMouseDown={stop}>
      <DropdownMenu open={!!day && !!anchor} onOpenChange={(open) => !open && onClose()}>
        <DropdownMenuContent
          anchor={anchor}
          side="top"
          align="center"
          sideOffset={6}
          className="w-52 rounded-xl p-1 shadow-2xl"
        >
          <DropdownMenuGroup>
            <DropdownMenuLabel className="px-2.5 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">
              {label} · {t('habitDay.missed')}
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => day && void markDay(habit._id, habit.title, day)}
              className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium"
            >
              <Check className="size-4 text-primary-text" />
              {t('habitDay.markDone')}
            </DropdownMenuItem>
            {photoOk && (
              <DropdownMenuItem
                onClick={async () => {
                  if (day && (await markDay(habit._id, habit.title, day))) setPhotoDay(day)
                }}
                className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium"
              >
                <Camera className="size-4 text-primary-text" />
                {t('habitDay.markWithPhoto')}
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {photoDay && (
        <PhotoCheckinSheet
          habitId={habit._id}
          title={habit.title}
          category={habit.category}
          date={photoDay}
          ask={habit.photoAsk ?? 'sometimes'}
          discover={habit.dot?.kind === 'photo' ? undefined : 'link'}
          onClose={() => setPhotoDay(null)}
        />
      )}
    </span>
  )
}

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()
